/**
 * Afwezigheid-abstractielaag + datum-hulpfuncties
 *
 * Fase 1: leest uit de handmatige invoer (Zustand store).
 *
 * [NMBRS-KOPPELING-HIER]
 * Fase 2: vervang bouwAfwezigheidMap door:
 *   import { haalAfwezigheidVoorWeek } from '../../../../src/nmbrs-client.js';
 *   De rest van de app hoeft NIET aangepast te worden.
 */

export const ALLE_DAGEN = ['ma', 'di', 'wo', 'do', 'vr', 'za', 'zo'];
const IDX_DAG = ['zo', 'ma', 'di', 'wo', 'do', 'vr', 'za']; // getDay() index → dagcode

export const DAG_KORT = { ma: 'Ma', di: 'Di', wo: 'Wo', do: 'Do', vr: 'Vr', za: 'Za', zo: 'Zo' };

// Volluit, met hoofdletter — voor kopregels en losstaande labels ("Maandag 14 juli").
// Stond drie keer los gedeclareerd (MijnRooster, TeamRooster, Weekrooster), byte-identiek;
// hier samengevoegd zodat een wijziging niet op twee van de drie schermen blijft hangen.
// Voor een dagnaam MIDDEN in een zin is er DAG_LANG_KN in Weekrooster.jsx — die heeft
// kleine letters en is bewust niet dezelfde constante.
export const DAG_LANG = { ma: 'Maandag', di: 'Dinsdag', wo: 'Woensdag', do: 'Donderdag', vr: 'Vrijdag', za: 'Zaterdag', zo: 'Zondag' };

// ── Datum-hulpfuncties ───────────────────────────────────────────────────────
//
// LET OP bij de `jaar = new Date().getFullYear()`-defaults hieronder (en bij de twee
// afwezigheid-maps verderop): die default geeft het KALENDERjaar, terwijl al deze functies
// een ISO-JAAR verwachten. Rond de jaarwisseling lopen die uiteen — op 1, 2 en 3 januari 2027
// zegt de kalender 2027 terwijl die dagen ISO-jaar 2026 zijn (week 53). Wie op de default
// leunt krijgt daar dus stil een week uit het verkeerde jaar terug.
// De default is daarom puur een vangnet, geen bruikbare waarde: elke aanroeper hoort een
// expliciet isoJaar mee te geven. Op dit moment doet ook élke aanroeper dat (geverifieerd
// over client/, server/ en de tests), dus de default vuurt nergens. Houd dat zo.

/**
 * Geeft de maandag van een ISO-weeknummer terug (Jan 4 is altijd in week 1).
 */
export function weekNaarMaandag(weeknummer, jaar = new Date().getFullYear()) {
  const jan4 = new Date(jaar, 0, 4);
  const dag = jan4.getDay() || 7;
  const week1Ma = new Date(jan4);
  week1Ma.setDate(jan4.getDate() - dag + 1);
  const result = new Date(week1Ma);
  result.setDate(week1Ma.getDate() + (weeknummer - 1) * 7);
  return result;
}

/**
 * Berekent welke dag-codes uit een datumbereik vallen in een gegeven week.
 */
export function berekenAfwezigheidDagen(vanDatum, totDatum, weeknummer, jaar = new Date().getFullYear()) {
  const maandag = weekNaarMaandag(weeknummer, jaar);
  const zondag = new Date(maandag);
  zondag.setDate(maandag.getDate() + 6);

  const van = new Date(vanDatum + 'T00:00:00');
  const tot = new Date(totDatum + 'T00:00:00');

  const start = van > maandag ? van : maandag;
  const eind  = tot < zondag  ? tot : zondag;

  const dagen = [];
  const cursor = new Date(start);
  while (cursor <= eind) {
    const code = IDX_DAG[cursor.getDay()];
    if (code) dagen.push(code);
    cursor.setDate(cursor.getDate() + 1);
  }
  return dagen;
}

/**
 * Geeft alle unieke dagcodes voor een datumbereik (ongeacht welke week).
 * Handig voor de verlof-check in de modal.
 */
export function berekenUniekeDagCodes(vanDatum, totDatum) {
  const van = new Date(vanDatum + 'T00:00:00');
  const tot = new Date(totDatum + 'T00:00:00');
  const codes = new Set();
  const cursor = new Date(van);
  while (cursor <= tot) {
    const code = IDX_DAG[cursor.getDay()];
    if (code) codes.add(code);
    cursor.setDate(cursor.getDate() + 1);
  }
  return [...ALLE_DAGEN.filter(d => codes.has(d))]; // vaste volgorde
}

/**
 * Formateert een Date als "26 mei" of "26 mei 2026".
 */
export function formatDatum(datum, metJaar = false) {
  return datum.toLocaleDateString('nl-NL', {
    day: 'numeric', month: 'short', ...(metJaar ? { year: 'numeric' } : {}),
  });
}

/**
 * Compact weekbereik voor het tab-label: "2 - 8 jun." (zelfde maand én jaar: maand één
 * keer aan het eind) of "30 jun. - 6 jul." (verschillende maand of jaar: maand bij allebei).
 * Gewoon koppelstreepje met spaties; month: 'short' (mét punt, via formatDatum).
 */
export function weekBereikKort(weeknummer, isoJaar = new Date().getFullYear()) {
  const ma = weekNaarMaandag(weeknummer, isoJaar);
  const zo = new Date(ma);
  zo.setDate(ma.getDate() + 6);
  const zelfdeMaand = ma.getMonth() === zo.getMonth() && ma.getFullYear() === zo.getFullYear();
  return zelfdeMaand
    ? `${ma.getDate()} - ${formatDatum(zo)}`
    : `${formatDatum(ma)} - ${formatDatum(zo)}`;
}

// ── Afwezigheid-map ──────────────────────────────────────────────────────────

/**
 * Converteert handmatige afwezigheid naar { 'Naam': Set(['ma','di']) }.
 * Ondersteunt twee formaten:
 *   A (nieuw): { vanDatum: 'JJJJ-MM-DD', totDatum: 'JJJJ-MM-DD' }
 *   B (oud):   { dagen: ['ma','di'] }
 *
 * [NMBRS-KOPPELING-HIER] — later: swap naar nmbrs-client.haalAfwezigheidVoorWeek()
 */
export function bouwAfwezigheidMap(afwezigheid, weeknummer = null, jaar = new Date().getFullYear()) {
  const result = {};
  for (const item of afwezigheid) {
    let dagen;
    if (item.vanDatum && weeknummer) {
      dagen = berekenAfwezigheidDagen(item.vanDatum, item.totDatum, weeknummer, jaar);
    } else if (item.dagen) {
      dagen = item.dagen;
    } else {
      continue;
    }
    if (!result[item.naam]) result[item.naam] = new Set();
    for (const dag of dagen) result[item.naam].add(dag);
  }
  return result;
}

// Zelfde dagen-afleiding als bouwAfwezigheidMap, maar bewaart het TYPE per dag i.p.v. een Set.
// Loopt parallel mee voor de balans-credit-labeling; raakt de planning (Set-map) NIET.
//   { 'Naam': { ma: 'verlof', di: 'ziekte', ... } }
export function bouwAfwezigheidTypeMap(afwezigheid, weeknummer = null, jaar = new Date().getFullYear()) {
  const result = {};
  for (const item of afwezigheid) {
    let dagen;
    if (item.vanDatum && weeknummer) {
      dagen = berekenAfwezigheidDagen(item.vanDatum, item.totDatum, weeknummer, jaar);
    } else if (item.dagen) {
      dagen = item.dagen;
    } else {
      continue;
    }
    if (!result[item.naam]) result[item.naam] = {};
    for (const dag of dagen) result[item.naam][dag] = item.type || 'anders';
  }
  return result;
}
